import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

const cardVariants = cva('', {
  variants: {
    tone: {
      default: 'rounded-lg border bg-card text-card-foreground shadow-sm has-[table]:overflow-hidden',
      mktSand: 'bg-mkt-sand text-mkt-ink',
      mktSandDeep: 'bg-mkt-sand-deep text-mkt-ink',
      mktSurface: 'border border-mkt-ink/8 bg-mkt-surface text-mkt-ink',
      mktWhite: 'bg-white text-mkt-ink shadow-mkt-card',
      mktFloat: 'bg-white text-mkt-ink shadow-mkt-float',
      raised:
        'rounded-lg border text-card-foreground bg-card shadow-[0_4px_16px_hsl(220_60%_7%/0.06)] dark:border-[hsl(var(--dark-blue-700))] dark:bg-[hsl(var(--dark-blue-800))] dark:shadow-[0_4px_20px_hsl(220_60%_4%/0.35)]',
      mint: 'rounded-lg border text-card-foreground border-[hsl(152_20%_88%)] bg-[hsl(152_30%_96%)] shadow-[0_4px_16px_hsl(220_60%_7%/0.06)] dark:border-[hsl(152_20%_18%)] dark:bg-[hsl(152_20%_12%)]',
      muted: 'rounded-lg border text-card-foreground bg-muted/50',
      success: 'rounded-lg border text-card-foreground border-emerald-200/70 bg-emerald-50 dark:border-emerald-900/60 dark:bg-emerald-950/30',
      warning: 'rounded-lg border text-card-foreground border-orange-200/70 bg-orange-50 dark:border-orange-900/60 dark:bg-orange-950/30',
      danger: 'rounded-lg border text-card-foreground border-red-200 bg-red-50/70 dark:border-red-900/60 dark:bg-red-950/30',
      dashed: 'rounded-lg border text-card-foreground border-dashed bg-card',
      highlight: 'rounded-lg border text-card-foreground border-primary/30 bg-gradient-to-br from-primary/10 via-card to-card shadow-sm',
    },
    interactive: {
      true: 'cursor-pointer transition-shadow hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
      false: '',
    },
    selected: {
      true: 'border-primary ring-1 ring-primary',
      false: '',
    },
    // Shown faded (e.g. something no longer available).
    dimmed: {
      true: 'opacity-60',
      false: '',
    },
  },
  defaultVariants: {
    tone: 'default',
    interactive: false,
    selected: false,
    dimmed: false,
  },
});

export type CardProps = React.HTMLAttributes<HTMLDivElement> &
  VariantProps<typeof cardVariants> & {
    // Kartın kendisi bağlantı (Link) gibi başka bir öğe olacaksa
    asChild?: boolean;
  };

const Card = React.forwardRef<HTMLDivElement, CardProps>(
  ({ className, tone, interactive, selected, dimmed, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'div';
    return (
      <Comp ref={ref} className={cn(cardVariants({ tone, interactive, selected, dimmed }), className)} {...props} />
    );
  }
);
Card.displayName = 'Card';
const CardHeader = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('flex flex-col space-y-2 p-8 pb-4', className)} {...props} />
  )
);
CardHeader.displayName = 'CardHeader';
const CardTitle = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLHeadingElement>>(
  ({ className, ...props }, ref) => (
    <h3
      ref={ref}
      className={cn('text-2xl font-bold leading-tight tracking-tight  text-foreground', className)}
      {...props}
    />
  )
);
CardTitle.displayName = 'CardTitle';
const CardDescription = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLParagraphElement>>(
  ({ className, ...props }, ref) => (
    <p ref={ref} className={cn('text-sm text-muted-foreground', className)} {...props} />
  )
);
CardDescription.displayName = 'CardDescription';
const CardContent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => <div ref={ref} className={cn('p-8 pt-4', className)} {...props} />
);
CardContent.displayName = 'CardContent';
const CardFooter = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => <div ref={ref} className={cn('flex items-center p-8 pt-4', className)} {...props} />
);
CardFooter.displayName = 'CardFooter';
export { Card, CardHeader, CardFooter, CardTitle, CardDescription, CardContent, cardVariants };
